import styled from '@emotion/styled';
import {
  type Placement,
  autoUpdate,
  flip,
  offset as floatingUiOffset,
  limitShift,
  shift,
  useFloating,
} from '@floating-ui/react-dom';
import React, { useEffect } from 'react';

import ReactDOM from 'react-dom';

import { useTransition } from 'transition-hook';

import { unit } from '../../variables';

type PlacementProps =
  | 'topLeft'
  | 'topCenter'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomCenter'
  | 'bottomRight'
  | 'leftTop'
  | 'leftCenter'
  | 'leftBottom'
  | 'rightTop'
  | 'rightCenter'
  | 'rightBottom';

const placements = {
  topLeft: 'top-start',
  topCenter: 'top',
  topRight: 'top-end',
  bottomLeft: 'bottom-start',
  bottomCenter: 'bottom',
  bottomRight: 'bottom-end',
  leftTop: 'left-start',
  leftCenter: 'left',
  leftBottom: 'left-end',
  rightTop: 'right-start',
  rightCenter: 'right',
  rightBottom: 'right-end',
};

const Wrapper = styled.div<{
  childrenZIndex: number;
  width: string;
}>`
  position: relative;
  // transform forces the popover to calculate the position from the trigger instead of the viewport
  transform: translate(0);
  width: ${props => props.width};
  z-index: ${props => props.childrenZIndex};
`;

const ContentWrapper = styled.div<{
  zIndex: string | number;
}>`
  ${props => props.zIndex && `z-index: ${props.zIndex}`};
`;

const Popover = ({
  children,
  content,
  offset = 0.5,
  boundaryOffset = 0.5,
  isOpen,
  zIndex = 500,
  childrenZIndex = 1,
  placement = 'topCenter',
  rootId,
  boundaryElement = 'body',
  transitionDuration = 0,
  transitionDelay = 0,
  width = 'fit-content',
  ...props
}: PopoverProps) => {
  const unitOffset = offset * unit;
  const unitBoundaryOffset = boundaryOffset * unit;
  const isBrowser = typeof window !== 'undefined';
  const rootNode =
    rootId && isBrowser ? document.getElementById(rootId) : undefined;

  const { stage, shouldMount } = useTransition(
    isOpen,
    transitionDuration + transitionDelay
  );
  const getBoundaryElement = () => {
    if (boundaryElement === 'body' && isBrowser) {
      return document.body;
    }

    return boundaryElement as Element;
  };

  const { x, y, reference, floating, strategy, update, refs } = useFloating({
    placement: placements[placement] as Placement,
    middleware: [
      shift({
        padding: unitBoundaryOffset,
        boundary: boundaryElement ? getBoundaryElement() : undefined,
        limiter: limitShift(),
      }),
      flip({
        fallbackPlacements: ['top', 'bottom'],
        fallbackStrategy: 'initialPlacement',
      }),
      floatingUiOffset(unitOffset),
    ],
    strategy: 'fixed',
  });

  useEffect(() => {
    if (!refs.reference.current || !refs.floating.current) {
      return;
    }

    // Only call this when the floating element is rendered
    return autoUpdate(refs.reference.current, refs.floating.current, update);
  }, [refs.reference, refs.floating, update, shouldMount]);

  const contentProps = {
    zIndex,
    ref: floating,
    style: {
      position: strategy,
      top: y ?? '',
      left: x ?? '',
      transition: `opacity ${transitionDuration}ms ${transitionDelay}ms`,
      opacity: stage === 'enter' ? 1 : 0,
    },
  };

  return (
    <Wrapper
      ref={reference}
      {...props}
      childrenZIndex={childrenZIndex}
      width={width}
    >
      {children}

      {shouldMount && (
        <>
          {!rootNode && (
            <ContentWrapper {...contentProps}>{content}</ContentWrapper>
          )}

          {rootNode &&
            ReactDOM.createPortal(
              <ContentWrapper {...contentProps}>{content}</ContentWrapper>,
              rootNode
            )}
        </>
      )}
    </Wrapper>
  );
};

type PopoverProps = {
  children?: React.ReactNode;
  content?: React.ReactNode;
  offset?: number;
  boundaryOffset?: number;
  zIndex?: number | string;
  isOpen?: boolean;
  placement?: PlacementProps;
  rootId?: string;
  boundaryElement?: 'body' | Element;
  transitionDuration?: number;
  transitionDelay?: number;
  childrenZIndex?: number;
  width?: string;
};

export default Popover;
